import React, { useState, useEffect } from 'react';
import { Users, Shield, Mail, Calendar, Trash2, Eye, EyeOff, RefreshCw, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { isSupabaseConfigured } from '../../config/supabase';

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [emailUsers, setEmailUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Load users from Supabase and localStorage
  const loadUsers = async () => {
    setLoading(true);
    setError('');

    try {
      // Load authenticated users from Supabase (if configured)
      let supabaseUsers = [];
      if (isSupabaseConfigured()) {
        try {
          // Note: In a real app, you'd need an admin API to list users
          // For now, we'll show the current user as an example
          if (currentUser) {
            supabaseUsers = [{
              id: currentUser.id,
              email: currentUser.email,
              name: currentUser.user_metadata?.full_name || currentUser.email,
              created_at: currentUser.created_at,
              last_sign_in_at: currentUser.last_sign_in_at,
              isAdmin: true, // You'd determine this from your user roles system
              type: 'authenticated'
            }];
          }
        } catch (err) {
          console.error('Error loading Supabase users:', err);
        }
      }

      // Load email-only users from localStorage
      const storedEmailUsers = JSON.parse(localStorage.getItem('survey_email_users') || '{}');
      
      // Get known authenticated emails (persistent list)
      const knownAuthEmails = JSON.parse(localStorage.getItem('known_auth_emails') || '[]');
      const authenticatedEmails = new Set([
        ...supabaseUsers.map(user => user.email.toLowerCase()),
        ...knownAuthEmails
      ]);
      
      const emailUsersList = Object.entries(storedEmailUsers)
        .filter(([email]) => !authenticatedEmails.has(email.toLowerCase()))
        .map(([email, userData]) => ({
          id: email,
          email: email,
          name: userData.name,
          created_at: userData.createdAt,
          last_sign_in_at: userData.lastActiveAt,
          isAdmin: false,
          type: 'email-only',
          voteCount: userData.voteCount || 0,
          isDuplicate: authenticatedEmails.has(email.toLowerCase())
        }));

      // Clean up any duplicates from localStorage
      const duplicateEmails = Object.keys(storedEmailUsers)
        .filter(email => authenticatedEmails.has(email.toLowerCase()));
      
      if (duplicateEmails.length > 0) {
        console.log('Found duplicate email users, cleaning up:', duplicateEmails);
        const cleanedUsers = { ...storedEmailUsers };
        duplicateEmails.forEach(email => delete cleanedUsers[email]);
        localStorage.setItem('survey_email_users', JSON.stringify(cleanedUsers));
        
        // Also clean up their votes
        const storedVotes = JSON.parse(localStorage.getItem('survey_votes') || '{}');
        const cleanedVotes = { ...storedVotes };
        duplicateEmails.forEach(email => delete cleanedVotes[email]);
        localStorage.setItem('survey_votes', JSON.stringify(cleanedVotes));
      }

      setUsers(supabaseUsers);
      setEmailUsers(emailUsersList);
    } catch (err) {
      setError(`Error loading users: ${err.message}`);
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps are deliberately narrow; adding them re-runs this effect on every render
  }, [currentUser]);

  // Delete email-only user
  const handleDeleteEmailUser = (email) => {
    if (!window.confirm(`Delete user "${email}"? This will also delete all their survey votes.`)) {
      return;
    }

    try {
      // Remove from localStorage
      const storedUsers = JSON.parse(localStorage.getItem('survey_email_users') || '{}');
      delete storedUsers[email];
      localStorage.setItem('survey_email_users', JSON.stringify(storedUsers));

      // Remove their votes from localStorage
      const storedVotes = JSON.parse(localStorage.getItem('survey_votes') || '{}');
      delete storedVotes[email];
      localStorage.setItem('survey_votes', JSON.stringify(storedVotes));

      // Refresh the list
      loadUsers();
    } catch (err) {
      setError(`Error deleting user: ${err.message}`);
    }
  };

  // Clean up duplicate users
  const handleCleanupDuplicates = () => {
    if (!window.confirm('Remove all email-only accounts that match authenticated user email addresses? This will delete their votes too.')) {
      return;
    }

    try {
      const storedUsers = JSON.parse(localStorage.getItem('survey_email_users') || '{}');
      const storedVotes = JSON.parse(localStorage.getItem('survey_votes') || '{}');
      
      // Get authenticated user emails (including persistent list)
      const knownAuthEmails = JSON.parse(localStorage.getItem('known_auth_emails') || '[]');
      const authenticatedEmails = new Set([
        ...users.map(user => user.email.toLowerCase()),
        ...knownAuthEmails
      ]);
      
      // Find duplicates
      const duplicateEmails = Object.keys(storedUsers)
        .filter(email => authenticatedEmails.has(email.toLowerCase()));
      
      if (duplicateEmails.length === 0) {
        alert('No duplicate users found.');
        return;
      }

      // Remove duplicates
      const cleanedUsers = { ...storedUsers };
      const cleanedVotes = { ...storedVotes };
      
      duplicateEmails.forEach(email => {
        delete cleanedUsers[email];
        delete cleanedVotes[email];
      });

      localStorage.setItem('survey_email_users', JSON.stringify(cleanedUsers));
      localStorage.setItem('survey_votes', JSON.stringify(cleanedVotes));

      alert(`Removed ${duplicateEmails.length} duplicate users: ${duplicateEmails.join(', ')}`);
      loadUsers();
    } catch (err) {
      setError(`Error cleaning up duplicates: ${err.message}`);
    }
  };

  // Show known authenticated emails
  const handleShowKnownEmails = () => {
    const knownAuthEmails = JSON.parse(localStorage.getItem('known_auth_emails') || '[]');
    if (knownAuthEmails.length === 0) {
      alert('No known authenticated emails stored yet.');
      return;
    }
    
    const emailList = knownAuthEmails.join('\n• ');
    alert(`Known Authenticated Emails (${knownAuthEmails.length}):\n\n• ${emailList}\n\nThese emails are blocked from creating email-only accounts.`);
  };

  // Clear known authenticated emails (dangerous)
  const handleClearKnownEmails = () => {
    const knownAuthEmails = JSON.parse(localStorage.getItem('known_auth_emails') || '[]');
    if (knownAuthEmails.length === 0) {
      alert('No known authenticated emails to clear.');
      return;
    }

    if (!window.confirm(`Clear all ${knownAuthEmails.length} known authenticated emails? This will allow those emails to create email-only accounts again. This should only be done if you're sure those emails are no longer used for authentication.`)) {
      return;
    }

    localStorage.removeItem('known_auth_emails');
    alert('Known authenticated emails cleared. Users can now create email-only accounts with those addresses.');
    loadUsers();
  };

  // Toggle admin status (placeholder - would need proper implementation)
  const handleToggleAdmin = (userId, currentStatus) => {
    // This would need to be implemented with proper user role management
    console.log(`Toggle admin status for user ${userId}: ${!currentStatus}`);
    alert('Admin role management would be implemented here with proper user roles system.');
  };

  if (!isSupabaseConfigured()) {
    return (
      <div className="bg-gray-800 border-2 border-orange-600 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Users className="text-orange-400" size={24} />
          <h2 className="text-xl font-bold text-orange-300">User Management</h2>
        </div>
        <div className="text-center py-8">
          <Users className="w-12 h-12 mx-auto text-gray-500 mb-4" />
          <h3 className="text-lg font-bold text-orange-300 mb-2">Supabase Required</h3>
          <p className="text-orange-200 mb-4">
            User management requires Supabase configuration. Please set up your Supabase credentials above.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 border-2 border-orange-600 rounded-lg p-6">
      <div className="flex items-center gap-2 mb-4">
        <Users className="text-orange-400" size={24} />
        <h2 className="text-xl font-bold text-orange-300">User Management</h2>
      </div>

      {error && (
        <div className="bg-red-900 border border-red-600 rounded-lg p-4 mb-4">
          <p className="text-red-200">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin w-8 h-8 border-4 border-orange-400 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-orange-200">Loading users...</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Authenticated Users */}
          {users.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-orange-300 mb-3 flex items-center gap-2">
                <Shield size={18} />
                Authenticated Users ({users.length})
              </h3>
              <div className="space-y-2">
                {users.map(user => (
                  <div key={user.id} className="bg-gray-700 border border-orange-500 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${user.isAdmin ? 'bg-red-500' : 'bg-green-500'}`}></div>
                        <div>
                          <div className="font-semibold text-white">{user.name}</div>
                          <div className="text-sm text-gray-300 flex items-center gap-2">
                            <Mail size={14} />
                            {user.email}
                          </div>
                          <div className="text-xs text-gray-400 flex items-center gap-2 mt-1">
                            <Calendar size={12} />
                            Joined: {new Date(user.created_at).toLocaleDateString()}
                            {user.last_sign_in_at && (
                              <span className="ml-2">
                                Last active: {new Date(user.last_sign_in_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          user.isAdmin 
                            ? 'bg-red-900 text-red-200' 
                            : 'bg-green-900 text-green-200'
                        }`}>
                          {user.isAdmin ? 'Admin' : 'User'}
                        </span>
                        {user.id !== currentUser?.id && (
                          <button
                            onClick={() => handleToggleAdmin(user.id, user.isAdmin)}
                            className="bg-blue-600 hover:bg-blue-500 text-white px-3 py-1 rounded text-xs font-semibold transition-colors"
                            title={`Make ${user.isAdmin ? 'User' : 'Admin'}`}
                          >
                            {user.isAdmin ? <EyeOff size={12} /> : <Shield size={12} />}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Email-Only Users */}
          <div>
            <h3 className="text-lg font-semibold text-orange-300 mb-3 flex items-center gap-2">
              <Mail size={18} />
              Email Survey Participants ({emailUsers.length})
            </h3>
            {emailUsers.length === 0 ? (
              <div className="text-center py-8 bg-gray-700 rounded-lg border border-gray-600">
                <Mail className="w-8 h-8 mx-auto text-gray-500 mb-2" />
                <p className="text-gray-400">No email survey participants yet</p>
                <p className="text-gray-500 text-sm">Users who join surveys with email will appear here</p>
              </div>
            ) : (
              <div className="space-y-2">
                {emailUsers.map(user => (
                  <div key={user.id} className="bg-gray-700 border border-orange-500 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                        <div>
                          <div className="font-semibold text-white">{user.name}</div>
                          <div className="text-sm text-gray-300 flex items-center gap-2">
                            <Mail size={14} />
                            {user.email}
                          </div>
                          <div className="text-xs text-gray-400 flex items-center gap-2 mt-1">
                            <Calendar size={12} />
                            Joined: {new Date(user.created_at).toLocaleDateString()}
                            {user.last_sign_in_at && (
                              <span className="ml-2">
                                Last active: {new Date(user.last_sign_in_at).toLocaleDateString()}
                              </span>
                            )}
                            <span className="ml-2">
                              Votes: {user.voteCount}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-900 text-blue-200">
                          Email Only
                        </span>
                        <button
                          onClick={() => handleDeleteEmailUser(user.email)}
                          className="bg-red-600 hover:bg-red-500 text-white px-3 py-1 rounded text-xs font-semibold transition-colors flex items-center gap-1"
                          title="Delete user and their votes"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* User Stats */}
          <div className="bg-gray-700 border border-orange-500 rounded-lg p-4">
            <h4 className="text-orange-300 font-semibold mb-2">User Statistics</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-white">{users.length}</div>
                <div className="text-xs text-gray-400">Authenticated</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{emailUsers.length}</div>
                <div className="text-xs text-gray-400">Email Only</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{users.length + emailUsers.length}</div>
                <div className="text-xs text-gray-400">Total Users</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white">
                  {emailUsers.reduce((sum, user) => sum + user.voteCount, 0)}
                </div>
                <div className="text-xs text-gray-400">Total Votes</div>
              </div>
            </div>
          </div>

          {/* Management Actions */}
          <div className="bg-gray-700 border border-orange-500 rounded-lg p-4">
            <h4 className="text-orange-300 font-semibold mb-3">Management Actions</h4>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={loadUsers}
                className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2"
              >
                <RefreshCw size={16} />
                Refresh Users
              </button>
              <button
                onClick={handleCleanupDuplicates}
                className="bg-yellow-600 hover:bg-yellow-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2"
                title="Remove email-only accounts that match authenticated users"
              >
                <Shield size={16} />
                Clean Duplicates
              </button>
              <button
                onClick={handleShowKnownEmails}
                className="bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2"
                title="View known authenticated email addresses"
              >
                <Eye size={16} />
                View Known Emails
              </button>
              <button
                onClick={handleClearKnownEmails}
                className="bg-orange-600 hover:bg-orange-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2"
                title="Clear known authenticated emails (dangerous)"
              >
                <AlertTriangle size={16} />
                Clear Known Emails
              </button>
              <button
                onClick={() => {
                  if (window.confirm('Clear all email survey data? This will delete all email users and their votes.')) {
                    localStorage.removeItem('survey_email_users');
                    localStorage.removeItem('survey_votes');
                    loadUsers();
                  }
                }}
                className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2"
              >
                <Trash2 size={16} />
                Clear All Email Data
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
